/**
 * Hand-rolled fetch for `GET /api/v1/users/me/effective-permissions`.
 *
 * Backend contract — confirmed via be-094 / CC-87 (08.1-01-SUMMARY):
 *   Path:     /api/v1/users/me/effective-permissions
 *   Response: 200 → string[] (flat sorted array of canonical
 *             `module:action[:scope]` codes)
 *   Auth:     isAuthenticated() only — no permission gate (avoids
 *             chicken-and-egg)
 *   Audience: both `admin` and `mobile` accepted
 *   Empty:    `[]` is valid for newly-provisioned users with no role grants —
 *             do NOT treat as error
 *
 * Justification for NOT using `@linkhr/api-client`'s `createApiClient`:
 *   The api-client middleware chain includes 401-retry logic that calls the
 *   TokenProvider's `tryRefresh`. This bootstrap fires DURING login + refresh;
 *   routing through api-client would create an infinite loop when the perms
 *   call returns 401 (refresh → fetch perms → 401 → refresh → …). Same
 *   boundary justification as `performRefresh.ts`.
 *
 * If a future api-client refactor exposes a non-retrying fetch path, this
 * module can swap to the typed `operations["myEffectivePermissions"]` hook
 * from `packages/api-client/src/generated/schema.d.ts`.
 *
 * Failure mode — fail-closed:
 *   On any non-2xx response or network error, returns `[]` and logs to
 *   console.error. Throwing would orphan the auth flow (login appears to
 *   succeed but the snapshot never commits). Empty perms ⇒ all gates deny ⇒
 *   user can still navigate to login-redirect targets, just not gated routes.
 */

export interface FetchEffectivePermissionsOpts {
  apiBaseUrl: string;
  accessToken: string;
  /** Test seam — defaults to global `fetch`. */
  fetcher?: typeof fetch;
}

const PATH = "/api/v1/users/me/effective-permissions";

export async function fetchEffectivePermissions(
  opts: FetchEffectivePermissionsOpts,
): Promise<readonly string[]> {
  const fetchImpl = opts.fetcher ?? fetch;
  try {
    const res = await fetchImpl(`${opts.apiBaseUrl}${PATH}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${opts.accessToken}`,
        Accept: "application/json",
      },
    });
    if (!res.ok) {
      // Defensive — never throw at bootstrap.
      console.error(`[auth] fetchEffectivePermissions failed: HTTP ${res.status}`);
      return [];
    }
    const body = (await res.json()) as unknown;
    if (Array.isArray(body)) {
      // Validate elements are strings (defensive — backend guarantees
      // string[] but a misbehaving proxy could inject other shapes).
      return body.filter((x): x is string => typeof x === "string");
    }
    // Defensive — accept envelope shape too in case backend's content
    // negotiation routes through a wrapper at some prod layer. Safe no-op
    // if the field is absent.
    if (
      body !== null &&
      typeof body === "object" &&
      "permissions" in body &&
      Array.isArray((body as { permissions: unknown }).permissions)
    ) {
      return (body as { permissions: unknown[] }).permissions.filter(
        (x): x is string => typeof x === "string",
      );
    }
    console.error("[auth] fetchEffectivePermissions: unexpected body shape", body);
    return [];
  } catch (e) {
    // Network / parse error — fail-closed.
    console.error("[auth] fetchEffectivePermissions network error", e);
    return [];
  }
}
